<script setup lang="ts">
type ApplicationData = App.Students.Data.ApplicationData;
type DataRequestData = App.DataRequests.Data.DataRequestData;
type EnumData = App.Base.Data.EnumData;
type PartialApplicationData = App.Students.Data.PartialApplicationData;

import type { Selectable } from '@/components/selectBox/selectBox';

import DataRequestSlideout from '@/domains/data-request/components/adviser/DataRequestSlideout.vue';
import DataRequestSlideoutConfirmationDialog from '@/domains/data-request/components/adviser/DataRequestSlideoutConfirmationDialog.vue';
import CollegeApplicationFormFields from '@/components/students/studentActions/CollegeApplicationFormFields.vue';
import DangerDialog from '@/components/dialog/DangerDialog.vue';

export type ShowDataRequestProps = {
  dataRequest: DataRequestData;
  application: ApplicationData | null;
  statuses: EnumData[];
};

const props = defineProps<ShowDataRequestProps>();

const [isRejectDialogOpen, rejectDialogToggle] = useToggle(false);
const [isIntendToEnrollDialogOpen, handleToggleIntendToEnrollDialog] = useToggle(false);

const dataRequestAttributes = computed(
  () => props.dataRequest.attributes as PartialApplicationData
);

const isPending = computed(() => props.dataRequest.status.value === 'pending');

const form = useForm({
  fields: {
    institute_id: (buildSelectableInstitute(dataRequestAttributes.value?.institute) ||
      buildSelectableInstitute(props.application?.institute)) as Selectable<number> | null,
    date_submitted:
      (dataRequestAttributes.value?.date_submitted || props.application?.date_submitted) ?? null,
    status: (dataRequestAttributes.value?.status ||
      props.application?.status) as Selectable<string> | null,
    intend_to_enroll: {
      label:
        dataRequestAttributes.value?.intend_to_enroll ??
        props.application?.intend_to_enroll ??
        false
          ? 'Yes'
          : 'No',
      value: !!(
        dataRequestAttributes.value?.intend_to_enroll ??
        props.application?.intend_to_enroll ??
        false
      )
    } as Selectable<boolean>,
    has_waiver: {
      label:
        dataRequestAttributes.value?.has_waiver ?? props.application?.has_waiver ?? false
          ? 'Yes'
          : 'No',
      value: !!(dataRequestAttributes.value?.has_waiver ?? props.application?.has_waiver ?? false)
    } as Selectable<boolean>,
    waiver: dataRequestAttributes.value?.waiver ?? props.application?.waiver ?? 0,
    first_choice:
      dataRequestAttributes.value?.first_choice ?? props.application?.first_choice ?? false,
    award_meeting_held:
      dataRequestAttributes.value?.award_meeting_held ??
      props.application?.award_meeting_held ??
      false,
    early_action:
      dataRequestAttributes.value?.early_action ?? props.application?.early_action ?? false,
    early_decision:
      dataRequestAttributes.value?.early_decision ?? props.application?.early_decision ?? false,
    sent_hs_transcript:
      dataRequestAttributes.value?.sent_hs_transcript ??
      props.application?.sent_hs_transcript ??
      false,
    applied_to_honors_program:
      dataRequestAttributes.value?.applied_to_honors_program ??
      props.application?.applied_to_honors_program ??
      false,
    financial_aid_submitted_to_school:
      dataRequestAttributes.value?.financial_aid_submitted_to_school ??
      props.application?.financial_aid_submitted_to_school ??
      false
  },
  transform: (fields) => ({
    ...fields,
    data_request_id: props.dataRequest.id,
    return_to_route: 'data-requests.index',
    institute_id: fields.institute_id?.value,
    status: fields.status?.value,
    intend_to_enroll: fields.intend_to_enroll?.value,
    has_waiver: fields.has_waiver?.value
  })
});

const rejectForm = useForm({
  method: 'POST',
  url: route('data-requests.reject', {
    dataRequest: props.dataRequest.id
  }),
  fields: {
    rejected_reason: ''
  },
  hooks: {
    success() {
      rejectDialogToggle();
    }
  }
});

function handleApproveDataRequestSubmit() {
  if (props.dataRequest.type.value === 'create') {
    form.submitWith({
      method: 'POST',
      url: route('students.college-applications.store', {
        student: props.dataRequest.user?.id
      })
    });
  }
  if (props.dataRequest.type.value === 'edit') {
    form.submitWith({
      method: 'PUT',
      url: route('students.college-applications.update', {
        application: props.dataRequest.requestable?.id,
        student: props.dataRequest.user?.id
      })
    });
  }
}

function handleApproveDataRequest() {
  if (form.fields.intend_to_enroll?.value && !props.application?.intend_to_enroll) {
    handleToggleIntendToEnrollDialog();
  } else {
    handleApproveDataRequestSubmit();
  }
}

function handleIntendToEnrollDialogConfirm() {
  handleApproveDataRequestSubmit();
  handleToggleIntendToEnrollDialog();
}

function handleRejectDataRequest() {
  rejectDialogToggle();
}

function handleRejectDataRequestConfirm() {
  rejectForm.submit();
}
</script>

<template>
  <DataRequestSlideout
    :dataRequest="dataRequest"
    :hasErrors="form.hasErrors"
    :isApproveLoading="form.processing"
    :isDirty="form.isDirty"
    @approveDataRequest="handleApproveDataRequest"
    @rejectDataRequest="handleRejectDataRequest"
  >
    <CollegeApplicationFormFields
      :application="application || undefined"
      :dataRequestType="dataRequest.type.value"
      :form="form"
      :isReadonly="!isPending"
      :statuses="statuses"
    />
  </DataRequestSlideout>
  <DataRequestSlideoutConfirmationDialog
    :form="rejectForm"
    :isOpen="isRejectDialogOpen"
    @onConfirm="() => handleRejectDataRequestConfirm()"
    @onCancel="() => rejectDialogToggle()"
    @onClose="() => rejectDialogToggle()"
  />
  <DangerDialog
    title="Intend to Enroll confirmation"
    message="Setting the 'Intend to Enroll' status to 'Yes' for this college application will deactivate this status for any other college application that currently has it activated. Are you sure you want to proceed?"
    :isOpen="isIntendToEnrollDialogOpen"
    confirmButtonLabel="Confirm Intend to Enroll"
    @onCancel="() => handleToggleIntendToEnrollDialog()"
    @onClose="() => handleToggleIntendToEnrollDialog()"
    @onConfirm="handleIntendToEnrollDialogConfirm"
  />
</template>
